<script context="module" lang="ts">
	import WYSIWYG from './../components/inputs/WYSIWYG.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import Button from '$lib/components/util/Button.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import { onMount } from 'svelte';
	import { page } from '$app/stores';

	export const config: SectionConfig = {
		collection: 'section_hero',
		availableFor: ['pages', 'special-pages'],
		query: gql`
      media {
        id
        type
        translations {
            languages_code {
                code
            }
            media_description
        }
      }
			translations {
				languages_code {
					code
				}
				title
				subtitle
				description
			}
			link {
				...Link
			}
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		subtitle: string;
		description: string;
		media: any;
		link: any;
	};
</script>

<script lang="ts">
	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	let firstVisit = !$page.data.visited;

	if (typeof window !== 'undefined' && firstVisit) {
		firstVisit = true;
		setTimeout(() => {
			setCookie('visited', true, 365);
		}, 1000);
	}

	function setCookie(cname: string, cvalue: any, exdays: number) {
		const d = new Date();
		d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
		let expires = 'expires=' + d.toUTCString();
		document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
	}

	onMount(() => {});
</script>

<div class="hero-section">
	<Section gapType="padding">
		<div class="background" slot="background">
			<div class="background-image-wrapper {$page.data.langDirection}">
				<Media {...data.media} fit="cover" lazyLoad="eager" />
			</div>
		</div>
		<div class="hero-wrapper">
			{#if data.title}
				<div class="hero-title">{data.title}</div>
			{/if}
			<div class="divider" />
			{#if data.subtitle}
				<div class="hero-subtitle">{data.subtitle}</div>
			{/if}
			{#if data.link}
				<div class="button-wrapper">
					<DirectusLink {...data.link}>
						<Button {...data.link} iconPos="after" size="xl" style="dark" />
					</DirectusLink>
				</div>
			{/if}
		</div>
	</Section>
	{#if firstVisit}
		<div class="entry-animation">
			<div class="bg" />
			<div class="logo">
				<GlobalMedia key="logo_large" lazyLoad="eager" />
				<div class="logo-overlay1" />
				<div class="logo-overlay2" />
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	.hero-section {
		position: relative;
	}

	.hero-section :global(section) {
		min-height: 60vh;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.hero-wrapper {
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: v(content-padding);
	}

	.hero-title {
		@include font(h1);
		max-width: 12ch;
		line-height: 95%;
		color: color(black);
		hyphens: none !important;
	}

	.divider {
		width: 2.8rem;
		height: 3px;
		background: color(black);
		margin-top: -0.1rem;
	}

	.hero-subtitle {
		@include font(text-lg);
		color: color(black);
		max-width: 30ch;
		line-height: 142%;
	}

	.background-image-wrapper {
		position: absolute;
		width: 100%;
		height: 100%;
		z-index: -1;

		&.rtl {
			transform: scaleX(-1);
		}
	}

	@keyframes logo {
		0% {
			transform: scale(1);
			opacity: 0.001;
		}
		40% {
			transform: scale(0.9);
			opacity: 1;
		}
		80% {
			transform: scale(0.9);
			opacity: 1;
		}
		100% {
			transform: scale(1);
			opacity: 0;
		}
	}

	@keyframes logo-overlay1 {
		0% {
			transform: scaleY(1);
		}
		100% {
			transform: scaleY(0);
		}
	}

	@keyframes logo-overlay2 {
		0% {
			transform: scaleY(0);
			opacity: 1;
		}
		99% {
			transform: scaleY(1);
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	@keyframes bg {
		0% {
			transform: scaleY(1);
		}
		100% {
			transform: scaleY(0);
		}
	}

	.entry-animation {
		position: absolute;
		z-index: 2;
		inset: 0;

		pointer-events: none;

		display: flex;
		justify-content: center;
		align-items: center;

		.bg {
			position: absolute;
			inset: 0;
			background: color(yellow);

			transform-origin: top;

			animation: bg 0.6s cubic-bezier(0.25, 1, 0.5, 1) 2.1s both;
			z-index: -1;
		}

		.logo {
			position: relative;
			height: 80%;
			overflow: hidden;

			animation: logo 1.7s cubic-bezier(0.25, 1, 0.5, 1) 0.5s both;
		}

		.logo-overlay1 {
			position: absolute;
			inset: 0;
			background: color(yellow);
			transform-origin: bottom;

			animation: logo-overlay1 0.6s cubic-bezier(0.25, 1, 0.5, 1) 0.6s both;
		}

		.logo-overlay2 {
			position: absolute;
			width: 100%;
			height: 100%;
			bottom: 0;
			inset-inline-start: 0;
			background: color(yellow);
			opacity: 1;

			transform-origin: bottom;

			animation: logo-overlay2 0.4s ease 1.8s both;
		}
	}

	@media (max-width: 768px) {
		.hero-section :global(section) {
			min-height: calc(100dvh - v(header__height));
			justify-content: flex-end;
		}

		.hero-wrapper {
			margin-bottom: v(content-padding);
		}
	}
</style>
